"use client";

import PropertyCard from "@/components/properties/PropertyCard";
import Pagination from "@/components/ui/Pagination";
import { SAUDI_CITIES } from "@/data/saudiCities";
import {
  usePropertiesBanner,
  usePropertyCities,
  usePropertyFilters,
  usePublicProperties,
} from "@/hooks/usePublicData";
import { useLocale } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useState } from "react";
import Riyal from "@/components/ui/Riyal";

const PropertiesMap = dynamic(
  () => import("@/components/properties/PropertiesMap"),
  {
    ssr: false,
    loading: () => <div className="h-full w-full animate-pulse bg-clay-50 rounded-3xl" />,
  },
);

function Icon({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

const LIMIT = 12;
const MAX_PRICE = 5000;
const PRICE_STEP = 100;

function ExploreInner() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const t = (a: string, e: string) => (isAr ? a : e);
  const { data: banner } = usePropertiesBanner();
  const SAR = <Riyal />;

  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();

  // ── Initial state from the URL ──
  const [search, setSearch] = useState(params.get("search") ?? "");
  const [debounced, setDebounced] = useState(search);
  const [minPrice, setMinPrice] = useState(params.get("minPrice") ?? "");
  const [maxPrice, setMaxPrice] = useState(params.get("maxPrice") ?? "");
  const [debMin, setDebMin] = useState(minPrice);
  const [debMax, setDebMax] = useState(maxPrice);
  const [types, setTypes] = useState<string[]>(
    (params.get("filterId") ?? "").split(",").filter(Boolean),
  );
  const [selectedCities, setSelectedCities] = useState<string[]>(
    (params.get("city") ?? "").split(",").filter(Boolean),
  );
  const [citySearch, setCitySearch] = useState("");
  const [sort, setSort] = useState(params.get("sort") ?? "newest");
  const [page, setPage] = useState(Number(params.get("page")) || 1);
  const [mapOpen, setMapOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: filters = [] } = usePropertyFilters();
  const { data: dynamicCities = [] } = usePropertyCities();

  // All cities = dashboard cities (with properties) + every KSA city, deduped.
  const allCities = useMemo(() => {
    const map = new Map<string, { ar: string; en: string }>();
    for (const c of dynamicCities)
      if (c?.ar) map.set(c.ar, { ar: c.ar, en: c.en || c.ar });
    for (const c of SAUDI_CITIES) if (!map.has(c.ar)) map.set(c.ar, c);
    return Array.from(map.values()).sort((a, b) => a.ar.localeCompare(b.ar, "ar"));
  }, [dynamicCities]);

  // Debounce the search box.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [search]);

  // Debounce the price slider.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebMin(minPrice);
      setDebMax(maxPrice);
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [minPrice, maxPrice]);

  const toggleType = (id: string) => {
    setTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setPage(1);
  };
  const onSort = (v: string) => {
    setSort(v);
    setPage(1);
  };
  const onCity = (v: string) => {
    setSelectedCities((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
    setPage(1);
  };
  const clearAll = () => {
    setSearch("");
    setTypes([]);
    setSelectedCities([]);
    setSort("newest");
    setMinPrice("");
    setMaxPrice("");
    setPage(1);
  };

  // Keep the URL in sync.
  useEffect(() => {
    const qs = new URLSearchParams();
    if (debounced.trim()) qs.set("search", debounced.trim());
    if (types.length) qs.set("filterId", types.join(","));
    if (selectedCities.length) qs.set("city", selectedCities.join(","));
    if (sort !== "newest") qs.set("sort", sort);
    if (debMin) qs.set("minPrice", debMin);
    if (debMax) qs.set("maxPrice", debMax);
    if (page > 1) qs.set("page", String(page));
    const q = qs.toString();
    router.replace(`${pathname}${q ? `?${q}` : ""}`, { scroll: false });
  }, [debounced, types, selectedCities, sort, debMin, debMax, page, pathname, router]);

  const { data: res, isLoading } = usePublicProperties({
    page,
    limit: LIMIT,
    search: debounced || undefined,
    sort,
    filterId: types.length ? types.join(",") : undefined,
    minPrice: debMin ? Number(debMin) : undefined,
    maxPrice: debMax ? Number(debMax) : undefined,
    city: selectedCities.length ? selectedCities.join(",") : undefined,
  });
  const properties = res?.data ?? [];
  const pagination = res?.pagination;

  const sortOptions = [
    { value: "newest", ar: "الأحدث", en: "Newest" },
    { value: "price_asc", ar: "السعر: الأقل", en: "Price: Low" },
    { value: "price_desc", ar: "السعر: الأعلى", en: "Price: High" },
  ];

  const lo = Math.max(0, Number(minPrice) || 0);
  const hi = maxPrice ? Math.min(MAX_PRICE, Number(maxPrice)) : MAX_PRICE;
  const onPrice = ([l, h]: [number, number]) => {
    setMinPrice(l > 0 ? String(l) : "");
    setMaxPrice(h < MAX_PRICE ? String(h) : "");
  };

  const activeCount =
    types.length +
    (debMin || debMax ? 1 : 0) +
    (debounced.trim() ? 1 : 0) +
    selectedCities.length;

  const cityName = (v: string) => {
    const c = allCities.find((x) => x.ar === v || x.en === v);
    return c ? (isAr ? c.ar : c.en) : v;
  };

  const cq = citySearch.trim().toLowerCase();
  const shownCities = cq
    ? allCities.filter(
        (c) => c.ar.includes(citySearch.trim()) || c.en.toLowerCase().includes(cq),
      )
    : allCities;

  const typeName = (id: string) => {
    const c = filters.find((x: any) => (x.id ?? x._id) === id);
    return c ? (isAr ? c.name?.ar : c.name?.en) : id;
  };

  // ── Filter panel (shared by desktop rail + mobile drawer) ──
  const renderFilters = () => (
    <div className="space-y-6">
      {/* Location / city — multi-select */}
      <div>
        <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-ink-900">
          <Icon name="location_on" className="text-clay-600 text-[18px]" />
          {t("المكان", "Location")}
          {selectedCities.length > 0 && (
            <span className="ms-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-clay-600 px-1.5 text-white text-[11px]">
              {selectedCities.length}
            </span>
          )}
        </h4>
        <div className="relative mb-2">
          <Icon
            name="search"
            className="absolute top-1/2 -translate-y-1/2 inset-s-2.5 text-ink-500 text-[16px] pointer-events-none"
          />
          <input
            value={citySearch}
            onChange={(e) => setCitySearch(e.target.value)}
            placeholder={t("ابحث عن مدينة…", "Search a city…")}
            className="w-full ps-8 pe-3 py-2 rounded-xl border border-clay-100 bg-surface text-sm text-ink-900 outline-none focus:border-clay-600 placeholder:text-ink-500/60"
          />
        </div>
        <div className="flex flex-col gap-0.5 max-h-56 overflow-y-auto pe-1">
          {shownCities.map((c) => {
            const val = c.ar;
            const active = selectedCities.includes(val);
            return (
              <button
                key={val}
                onClick={() => onCity(val)}
                className="flex items-center gap-3 rounded-xl px-2.5 py-2 text-sm text-ink-900 hover:bg-clay-50 transition-colors"
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors ${
                    active ? "border-clay-600 bg-clay-600 text-white" : "border-clay-200"
                  }`}
                >
                  {active && <Icon name="check" className="text-[14px]" />}
                </span>
                <span className={active ? "font-bold" : ""}>
                  {isAr ? c.ar : c.en}
                </span>
              </button>
            );
          })}
          {shownCities.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-ink-500">
              {t("لا توجد مدينة مطابقة", "No matching city")}
            </p>
          )}
        </div>
        {selectedCities.length > 0 && (
          <button
            onClick={() => setSelectedCities([])}
            className="mt-1 text-start text-xs font-bold text-clay-600 hover:underline"
          >
            {t("إلغاء تحديد المدن", "Clear cities")}
          </button>
        )}
      </div>

      <div className="h-px bg-clay-100" />

      {/* Property type — multi-select */}
      <div>
        <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-ink-900">
          <Icon name="home_work" className="text-clay-600 text-[18px]" />
          {t("نوع الوحدة", "Property type")}
        </h4>
        <div className="flex flex-col gap-1">
          {filters.map((c: any) => {
            const id = c.id ?? c._id;
            const active = types.includes(id);
            return (
              <button
                key={id}
                onClick={() => toggleType(id)}
                className="flex items-center gap-3 rounded-xl px-2.5 py-2 text-sm text-ink-900 hover:bg-clay-50 transition-colors"
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors ${
                    active
                      ? "border-clay-600 bg-clay-600 text-white"
                      : "border-clay-200"
                  }`}
                >
                  {active && <Icon name="check" className="text-[14px]" />}
                </span>
                <span className={active ? "font-bold" : ""}>
                  {isAr ? c.name?.ar : c.name?.en}
                </span>
              </button>
            );
          })}
          {types.length > 0 && (
            <button
              onClick={() => setTypes([])}
              className="mt-1 text-start text-xs font-bold text-clay-600 hover:underline"
            >
              {t("إلغاء تحديد الكل", "Deselect all")}
            </button>
          )}
        </div>
      </div>

      <div className="h-px bg-clay-100" />

      {/* Price — dual slider */}
      <div>
        <h4 className="mb-4 flex items-center gap-2 text-sm font-bold text-ink-900">
          <Icon name="payments" className="text-clay-600 text-[18px]" />
          {t("السعر / ليلة", "Price / night")}
        </h4>
        <div dir="ltr">
          <div className="range-dual">
            <div className="rd-track" />
            <div
              className="rd-fill"
              style={{
                left: `${(lo / MAX_PRICE) * 100}%`,
                right: `${100 - (hi / MAX_PRICE) * 100}%`,
              }}
            />
            <input
              type="range"
              min={0}
              max={MAX_PRICE}
              step={PRICE_STEP}
              value={lo}
              onChange={(e) =>
                onPrice([
                  Math.min(Number(e.target.value), hi - PRICE_STEP),
                  hi,
                ])
              }
            />
            <input
              type="range"
              min={0}
              max={MAX_PRICE}
              step={PRICE_STEP}
              value={hi}
              onChange={(e) =>
                onPrice([
                  lo,
                  Math.max(Number(e.target.value), lo + PRICE_STEP),
                ])
              }
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="rounded-lg bg-clay-50 px-2.5 py-1 text-xs font-bold text-ink-900">
            {lo.toLocaleString()} {SAR}
          </span>
          <span className="rounded-lg bg-clay-50 px-2.5 py-1 text-xs font-bold text-ink-900">
            {hi >= MAX_PRICE ? `${MAX_PRICE.toLocaleString()}+` : hi.toLocaleString()}{" "}
            {SAR}
          </span>
        </div>
      </div>

      <div className="h-px bg-clay-100" />

      {/* Sort */}
      <div>
        <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-ink-900">
          <Icon name="sort" className="text-clay-600 text-[18px]" />
          {t("الترتيب", "Sort by")}
        </h4>
        <div className="flex flex-col gap-1">
          {sortOptions.map((o) => (
            <button
              key={o.value}
              onClick={() => onSort(o.value)}
              className="flex items-center gap-3 rounded-xl px-2.5 py-2 text-sm text-ink-900 hover:bg-clay-50 transition-colors"
            >
              <span
                className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                  sort === o.value ? "border-clay-600" : "border-clay-200"
                }`}
              >
                {sort === o.value && (
                  <span className="h-2 w-2 rounded-full bg-clay-600" />
                )}
              </span>
              <span className={sort === o.value ? "font-bold" : ""}>
                {isAr ? o.ar : o.en}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const bannerTitle = (isAr ? banner?.title?.ar : banner?.title?.en) || t("استكشف الوحدات", "Explore units");
  const bannerSubtitle = isAr ? banner?.subtitle?.ar : banner?.subtitle?.en;

  return (
    <div className="najdi bg-background text-ink-900" dir={isAr ? "rtl" : "ltr"}>
      {/* ── Compact hero (background, text and logo from the dashboard) ── */}
      <section className="relative h-48 md:h-60 w-full flex items-center justify-center overflow-hidden md:-mt-[72px] md:pt-[72px]">
        {banner?.image ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={banner.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/45" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-clay-700 to-ink-900" />
        )}
        <div className="relative z-10 text-center px-6">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <h1 className="font-display text-3xl md:text-5xl font-extrabold text-white drop-shadow-lg">{bannerTitle}</h1>
            {banner?.showLogo && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src="/light-logo.png" alt="Diriyah Homes" className="h-7 md:h-10 w-auto drop-shadow-lg" />
            )}
          </div>
          {bannerSubtitle && <p className="mt-2 text-sm md:text-base text-white/85">{bannerSubtitle}</p>}
        </div>
      </section>

      {/* ── Top toolbar ── */}
      <div className="sticky top-[72px] z-40 bg-surface/95 backdrop-blur-md border-b border-clay-100 py-3 shadow-sm">
        <div className="container mx-auto px-4 md:px-6 flex items-center gap-2 md:gap-3">
          {/* Icon + field in one flex row → stays right in RTL and LTR, never overlaps the text */}
          <label className="flex h-11 min-w-0 flex-1 max-w-xl items-center gap-2 rounded-full border border-clay-100 bg-surface px-4 transition-colors focus-within:border-clay-600">
            <Icon name="search" className="shrink-0 text-ink-500 text-[20px]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("ابحث بالاسم أو المدينة", "Search by name or city")}
              aria-label={t("بحث", "Search")}
              className="h-full min-w-0 flex-1 bg-transparent text-sm text-ink-900 outline-none placeholder:text-ink-500/70"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} aria-label={t("مسح", "Clear")} className="shrink-0 text-ink-500 hover:text-ink-900">
                <Icon name="close" className="text-[18px]" />
              </button>
            )}
          </label>

          {pagination?.total != null && (
            <p className="hidden md:block text-sm text-ink-500 shrink-0">
              <span className="font-bold text-ink-900">{pagination.total}</span>{" "}
              {t("نتيجة", "results")}
            </p>
          )}

          <button
            onClick={() => setFiltersOpen(true)}
            className="lg:hidden flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-clay-100 bg-surface px-3.5 text-sm font-bold text-ink-900"
          >
            <Icon name="tune" className="text-[18px]" />
            {t("الفلاتر", "Filters")}
            {activeCount > 0 && (
              <span className="ms-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-clay-600 text-white text-[11px]">
                {activeCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setMapOpen(true)}
            aria-label={t("الخريطة", "Map")}
            className="flex h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-full border border-clay-600 text-clay-600 px-3 sm:px-4 text-sm font-bold hover:bg-clay-50 transition-colors"
          >
            <Icon name="map" className="text-[18px]" />
            <span className="hidden sm:inline">{t("الخريطة", "Map")}</span>
          </button>
        </div>
      </div>

      {/* ── Body: sidebar + results ── */}
      <main className="container mx-auto px-4 md:px-6 py-8">
        <div className="flex gap-8">
          {/* Desktop filter rail */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-32">
              <div className="rounded-3xl border border-clay-100 bg-surface shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-clay-100 bg-clay-50/40">
                  <h3 className="flex items-center gap-2 font-display text-base font-bold text-ink-900">
                    <Icon name="tune" className="text-clay-600 text-[20px]" />
                    {t("الفلاتر", "Filters")}
                  </h3>
                  {activeCount > 0 && (
                    <button
                      onClick={clearAll}
                      className="text-xs font-bold text-clay-600 hover:underline"
                    >
                      {t("مسح الكل", "Clear all")}
                    </button>
                  )}
                </div>
                <div className="p-5">{renderFilters()}</div>
              </div>
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0">
            {activeCount > 0 && (
              <div className="mb-5 flex flex-wrap items-center gap-2">
                {debounced.trim() && (
                  <FilterChip label={`"${debounced.trim()}"`} onRemove={() => setSearch("")} />
                )}
                {selectedCities.map((cv) => (
                  <FilterChip key={cv} label={cityName(cv)} onRemove={() => onCity(cv)} />
                ))}
                {types.map((id) => (
                  <FilterChip key={id} label={typeName(id)} onRemove={() => toggleType(id)} />
                ))}
                {(debMin || debMax) && (
                  <FilterChip
                    label={
                      <>
                        {lo.toLocaleString()} – {hi >= MAX_PRICE ? `${MAX_PRICE}+` : hi.toLocaleString()} {SAR}
                      </>
                    }
                    onRemove={() => {
                      setMinPrice("");
                      setMaxPrice("");
                    }}
                  />
                )}
                <button
                  onClick={clearAll}
                  className="text-xs font-bold text-ink-500 hover:text-clay-600"
                >
                  {t("مسح الكل", "Clear all")}
                </button>
              </div>
            )}

            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-16/10 rounded-3xl bg-clay-100/40 mb-4" />
                    <div className="h-4 w-2/3 bg-clay-100/40 rounded mb-2" />
                    <div className="h-3 w-1/2 bg-clay-100/40 rounded" />
                  </div>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
                <Icon name="search_off" className="text-ink-500 text-5xl" />
                <p className="text-ink-500 font-bold">
                  {t("لا توجد وحدات مطابقة", "No stays match your filters")}
                </p>
                <button onClick={clearAll} className="text-clay-600 font-bold text-sm">
                  {t("مسح الفلاتر", "Clear filters")}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {properties.map((p: any) => (
                  <PropertyCard key={p._id || p.id} p={p} locale={locale} isAr={isAr} />
                ))}
              </div>
            )}

            <Pagination
              page={page}
              totalPages={pagination?.totalPages ?? 0}
              onChange={setPage}
              isAr={isAr}
            />
          </div>
        </div>
      </main>

      {/* ── Mobile filter drawer ── */}
      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-shade/40 backdrop-blur-sm"
            onClick={() => setFiltersOpen(false)}
          />
          <div className="absolute inset-y-0 inset-s-0 w-[86%] max-w-sm bg-background shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-clay-100">
              <h3 className="font-display text-lg font-bold text-ink-900">
                {t("الفلاتر", "Filters")}
              </h3>
              <button
                onClick={() => setFiltersOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-clay-50 text-ink-900"
              >
                <Icon name="close" className="text-[22px]" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">{renderFilters()}</div>
            <div className="p-4 border-t border-clay-100 flex gap-3">
              <button
                onClick={clearAll}
                className="flex-1 rounded-full border border-clay-100 py-3 text-sm font-bold text-ink-900"
              >
                {t("مسح الكل", "Clear all")}
              </button>
              <button
                onClick={() => setFiltersOpen(false)}
                className="flex-1 rounded-full bg-clay-600 text-white py-3 text-sm font-bold"
              >
                {t("عرض النتائج", "Show results")}
                {pagination?.total != null ? ` (${pagination.total})` : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Map overlay (full screen) ── */}
      {mapOpen && (
        <div className="fixed inset-0 z-[70] bg-background">
          <PropertiesMap properties={properties} locale={locale} isAr={isAr} />
          {/* Clear close button — top-right, above Leaflet controls */}
          <button
            onClick={() => setMapOpen(false)}
            className="absolute top-4 right-4 z-[1000] flex items-center gap-2 rounded-full bg-clay-600 text-white px-5 py-3 text-sm font-bold shadow-xl hover:bg-clay-700 transition-colors"
          >
            <Icon name="close" className="text-[20px]" />
            {t("إغلاق الخريطة", "Close map")}
          </button>
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, onRemove }: { label: React.ReactNode; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-clay-100 text-clay-700 ps-3 pe-2 py-1.5 text-xs font-bold">
      {label}
      <button onClick={onRemove} className="hover:text-clay-900" aria-label="remove">
        <Icon name="close" className="text-[15px]" />
      </button>
    </span>
  );
}

export default function ExploreClient() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <ExploreInner />
    </Suspense>
  );
}
